import React, { useState, useMemo, useEffect, useRef } from "react";
import { MdEdit, MdDelete } from "react-icons/md";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Table from "../../../components/common/Table/Table";
import Button from "../../../components/common/Button/Button";
import InputSearch from "../../../components/common/Inputs/InputSearch/InputSearch";
import { FiDownload } from "react-icons/fi";
import { GrAdd } from "react-icons/gr";
import { IoMdAdd } from "react-icons/io";
import { LuArrowDownUp } from "react-icons/lu";
import { getApiInstance } from "../../../utils/api";
import { mapWithReferences, mapList, sortByProperty } from "../../../utils/utils";
import { useZone } from "../../../context/ZoneContext";
import { ZONE_LABEL_MAP } from "../../../constants/zones";
import { getZoneLabel } from "../../../utils/zoneLabels";
import { useTranslation } from "react-i18next";

export default function Provincias({ user }) {
    const { t } = useTranslation();
    const tableRef = useRef(null);
    const canAdd = Number(user.codAcceso) === 1 && Number(user.role) !== 4;
    const [provincias, setProvincias] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const { selectedZone } = useZone();

    const provinceLabel = t("fields.province");

    const communityLabel = t("fields.community");

    const filterFields = [
        { key: "provincia", label: provinceLabel, type: "multiselect" },
        { key: "comunidad", label: communityLabel, type: "multiselect" },
        { key: "pais", label: t("fields.country"), type: "multiselect" },
        { key: "codZona", label: "Zona", type: "multiselect", labelMap: ZONE_LABEL_MAP },
    ];

    useEffect(() => {
        fetchPaises();
        fetchComunidades();
        fetchProvincias();
    }, []);

    const columnasProvincias = [
        { key: "id", label: "Provincia", visible: false },
        { key: "codProvincia", label: "Provincia", visible: false },
        {
            key: "provincia",
            label: "Provincia",
            visible: true,
            sortable: true,
        },
        { key: "codComunidad", label: "Comunidad", visible: false },
        {
            key: "comunidad",
            label: "Comunidad",
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "pais",
            label: "País",
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        { key: "codPais", label: "País", visible: false },
    ];

    useEffect(() => {
        fetchPaises();
        fetchComunidades();
        fetchProvincias();
    }, []);

    const fetchComunidades = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let comunidadesData = [];

            const response = await api.get(
                `/api/Comunidades/GetComunidades`
            );
            comunidadesData = response.data;

            setComunidades([
                ...mapList(
                    comunidadesData,
                    "comunidad",
                    "codComunidad",
                    "codComunidad",
                    "codPais"
                ),
            ]);

            console.log("Comunidades data fetched:", comunidadesData);

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPaises = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let paisesData = [];

            const response = await api.get(
                `/api/Paises/GetPaises`
            );
            paisesData = response.data;

            setPaises(mapList(paisesData, "pais", "codPais", "codPais"));

            console.log("Paises data fetched:", paisesData);

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchProvincias = async () => {
        setLoading(true);
        const api = getApiInstance();
        try {
            let provinciasData = [];

            const response = await api.get(
                `/api/Provincias/GetProvincias`
            );
            provinciasData = response.data;

            setProvincias(provinciasData);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const provinciasOrdenadas = useMemo(
        () => sortByProperty(provincias ?? [], "provincia"),
        [provincias]
    );

    if (error)
        return (
            <div className="muebles-pages__error-message">Error: {error}</div>
        );

    return (
        <PageLayout
            title={t("provinces.title")}
            subtitle={t("provinces.subtitle")}
            actions={[
                ...(canAdd
                    ? [
                          <Button
                              key="add"
                              variant="filled"
                              startIcon={IoMdAdd({})}
                              onClick={() => tableRef.current?.openAddModal()}
                              endIcon={undefined}
                          >
                              {`${t("common.add")} ${provinceLabel}`}
                          </Button>,
                      ]
                    : []),
            ]}
        >
            <Table
                ref={tableRef}
                hideAddButton={true}
                columns={columnasProvincias}
                dataTable={provinciasOrdenadas}
                showCheckboxes={false}
                expandable={true}
                expandableBreakpoint={600}
        noResultsMessage={t("provinces.noProvincesFound")}
        actions={["Eliminar", "Editar"]}
        searchTerms={["provincia", "comunidad", "pais"]}
                elementName={provinceLabel}
                formElements={[
                    {
                        elements: [
                            {
                                name: "provincia",
                                type: "text",
                                label: provinceLabel,
                                placeholder: t("provinces.enterProvince"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codPais",
                                type: "select",
                                label: t("fields.country"),
                                placeholder: t("placeholders.selectCountry"),
                                options: paises,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codComunidad",
                                type: "select",
                                label: communityLabel,
                                placeholder: t("placeholders.selectCommunity"),
                                options: comunidades,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                                associatedFormElement: "codPais",
                            },
                        ],
                    },
                ]}
                filterFields={filterFields}
                showAdvancedFilters={false}
                loading={loading}
                keyProp="codProvincia"
                sortProp="provincia"
                addApiUrl="/api/Provincias/SetProvincia"
                editApiUrl="/api/Provincias/EditProvincia"
                deleteApiUrl="/api/Provincias/DeleteProvincia"
                userCodAcceso={Number(user.codAcceso)}
                userCodPerfil={Number(user.role)}
            />
        </PageLayout>
    );
}
